import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND } from "@/data/translations";
import { LangToggle } from "@/components/LangToggle";

const LegalHeader = ({ back }) => (
    <header className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-5 flex items-center justify-between">
            <Link to="/" data-testid="legal-back-link" className="flex items-center gap-3 group">
                <picture>
                    <source srcSet={BRAND.logoDarkWebp} type="image/webp" />
                    <img src={BRAND.logoDark} alt="Fit & Wellness" className="h-9 md:h-10 w-auto" decoding="async" width="120" height="40" />
                </picture>
            </Link>
            <div className="flex items-center gap-4">
                <LangToggle testid="legal-language-toggle" />
                <Link
                    to="/"
                    className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-fw transition-colors"
                >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                    {back}
                </Link>
            </div>
        </div>
    </header>
);

const LegalSections = ({ sections, testidPrefix }) => (
    <div className="space-y-10">
        {sections.map((s, i) => (
            <section key={s.h} data-testid={`${testidPrefix}-section-${i}`}>
                <h2 className="font-display text-xl md:text-2xl font-semibold text-white mb-3 uppercase tracking-tight">
                    {s.h}
                </h2>
                <p className="text-base text-white/75 leading-relaxed">
                    {s.p}
                </p>
            </section>
        ))}
    </div>
);

const LegalPage = ({ data, testidPrefix }) => {
    const { lang } = useLanguage();
    const date = lang === "it" ? "Dicembre 2025" : "December 2025";

    return (
        <div className="min-h-screen bg-black text-white">
            <LegalHeader back={data.back} />

            <main className="mx-auto max-w-[860px] px-5 md:px-10 py-16 md:py-24">
                <div className="flex items-center gap-3 mb-6">
                    <span className="w-10 h-px bg-fw" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw">
                        Legal
                    </span>
                </div>

                <h1
                    data-testid={`${testidPrefix}-title`}
                    className="font-heavy text-5xl md:text-6xl lg:text-7xl leading-[0.95] uppercase mb-4"
                >
                    {data.title}
                </h1>
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/55 mb-14">
                    {data.updated}: {date}
                </p>

                <LegalSections sections={data.sections} testidPrefix={testidPrefix} />

                <div className="mt-20 pt-10 border-t border-white/10">
                    <Link
                        to="/"
                        data-testid={`${testidPrefix}-back-cta`}
                        className="inline-flex items-center gap-3 px-6 py-3.5 bg-fw text-black font-display font-semibold text-[11px] uppercase tracking-[0.18em] rounded-sm hover:bg-fw-glow hover:glow-green transition-all duration-300"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                        {data.back}
                    </Link>
                </div>
            </main>
        </div>
    );
};

export const Privacy = () => {
    const { t } = useLanguage();
    return <LegalPage data={t.privacy} testidPrefix="privacy" />;
};

export const Terms = () => {
    const { t } = useLanguage();
    return <LegalPage data={t.terms} testidPrefix="terms" />;
};
